/**
 * @author Mr_FabiozZz[mr.fabiozzz@gmail.com]
 */
import React, { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from "react";
import HeadPage from "pages/StepPages/StepFour/HeadPage";
import sass from "./areasActivity.module.sass";
import { Button } from "components/shared";
import ModalPortal from "components-new/ModalPortal";
import { BUTTON_VARIANTS } from "components/shared/Button/Button";
import InputTemp from "components-new/InputTemp";
import { useValidate } from "hooks/useValidate";
import { ReactComponent as ActionMenuIcon } from "images/ActionMenuIcon.svg";
import DropMenu from "pages/StepPages/StepFour/One/Tables/TableNavigate/DropMenu";
import { snackWrapper } from "helpers";
import { useLocation } from "react-router-dom";
import { checkCompletingReferences } from "toolkit/references/references.thunks";
import { useAppDispatch } from "toolkit/hooks";
import { directoryAPI } from "libs/apiClient/Directory";
import { userAPI } from "libs/apiClient/User";

// TODO: Пока не обрабатывается дубли в базе, нет проверки на уникальность name

const initial: ITypeOwnership = {
	name: "",
};
const Modal: React.FC<{
	rootFunc: Dispatch<SetStateAction<ITypeOwnership[]>> | Dispatch<SetStateAction<ITypeOwnership[] | null>>;
	trigger: { active: boolean; data: ITypeOwnership | null };
	cancelWindow: () => void;
}> = ({ trigger, rootFunc, cancelWindow }) => {
	const [newType, setNewType] = useState<ITypeOwnership>(initial);
	const dispatch = useAppDispatch();
	useEffect(() => {
		if (trigger.data) {
			setNewType(trigger.data);
		} else {
			setNewType(initial);
		}
	}, [trigger]);

	const send = () => {
		if (!trigger.data) {
			directoryAPI.activityAreasCreateNewActivityAreaCreate({ ...newType, sequenceNumber: "" }).then((r) => {
				snackWrapper("success", "Запись успешно создана");
				rootFunc((prevState: any) => [...prevState, r.data]);
				cancelWindow();
				dispatch(checkCompletingReferences());
			});
		} else {
			const { id, ...rest } = newType;
			directoryAPI.activityAreasActivityAreaIdEditCreate(id!, { ...rest, sequenceNumber: "" }).then((r) => {
				snackWrapper("success", "Запись успешно отредактирована");
				rootFunc((prevState: any) => [...prevState].map((_) => (_.id === r.data.id ? { ...r.data } : _)));
				cancelWindow();
				dispatch(checkCompletingReferences());
			});
		}
	};

	const { validate, error } = useValidate(newType, ["name"], send);

	const changeField = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		const { name, value } = e.target;
		setNewType((prevState) => ({ ...prevState, [name]: value }));
	};
	return trigger.active ? (
		<ModalPortal
			title={trigger.data ? "Редактировать сферу деятельности" : "Создать сферу деятельности"}
			handleClose={cancelWindow}>
			<div className={sass.modal}>
				<InputTemp
					onChange={changeField}
					name={"name"}
					error={error && !newType.name}
					value={newType.name}
					placeholder={"Введите текст"}
					label={"Статус"}
					required
				/>
				<div className={sass.btns}>
					<Button onClick={cancelWindow} variant={BUTTON_VARIANTS.OUTLINED}>
						Отменить
					</Button>
					<Button onClick={validate}>Готово</Button>
				</div>
			</div>
		</ModalPortal>
	) : null;
};

interface ITypeOwnership {
	id?: number;
	name: string;
}

const AreasOfActivity = () => {
	const location = useLocation();
	const dispatch = useAppDispatch();
	const [data, setData] = useState<ITypeOwnership[]>([]);

	const [filter, setFilter] = useState<ITypeOwnership[] | null>(null);

	useEffect(() => {
		directoryAPI.activityAreasList().then((r) => setData(r.data));
	}, []);
	const [modal, setModal] = useState<{ active: boolean; data: ITypeOwnership | null }>({
		active: false,
		data: null,
	});
	const [target, setTarget] = useState<HTMLOrSVGElement | undefined>();

	const toggleTarget = (e: React.MouseEvent<HTMLOrSVGElement>) => {
		e.stopPropagation();
		target ? setTarget(undefined) : setTarget(e.currentTarget);
	};
	const removeItem = () => {
		const id = target?.dataset.id;
		/* путь для физического удаления записи в БД */
		// $api.post(`/directory/activity-areas/${id}/delete`).then(() => {

		/* путь для отправки записи "в архив" в БД */
		directoryAPI
			.activityAreasActivityAreaIdDisableCreate(+id!)
			.then(() => {
				setData((prevState) => [...prevState].filter((_) => _.id !== Number(id)));
				snackWrapper("success", "Запись удалена");
				setTarget(undefined);
				dispatch(checkCompletingReferences());
			})
			.catch((error) => {
				if (error.response.status === 500) {
					snackWrapper("error", "Удаление невозможно, этот тип уже используется");
				}
				setTarget(undefined);
			});
	};
	const menuRef = useRef<any>(null);

	useEffect(() => {
		function handleClickOutside(event: any) {
			if (menuRef.current && target && !menuRef.current.contains(event.target)) {
				setTarget(undefined);
			}
		}

		document.addEventListener("mouseup", handleClickOutside);
		return () => {
			document.removeEventListener("mouseup", handleClickOutside);
		};
	}, [target]);

	const outlineStyle = useCallback(
		(id: number) => {
			return location.state && location.state.outline && location.state.outline === id ? sass.outline : "";
		},
		[location],
	);
	const [disabled, setDisabled] = useState(false);
	const needRule = useCallback(() => {
		if (disabled) snackWrapper("error", "Не достаточно прав");
	}, [disabled]);
	useEffect(() => {
		userAPI.permissionsCheckCreate(0, ["dictionary.activity_area.edit"]).then((r) => setDisabled(!r.data.length));
	}, []);
	return (
		<div className={sass.wrapper}>
			<DropMenu ref={menuRef} target={target} click={disabled ? needRule : removeItem} />
			<Modal
				rootFunc={filter && filter.length ? setFilter : setData}
				trigger={modal}
				cancelWindow={() => setModal((prevState) => ({ ...prevState, active: false }))}
			/>
			<HeadPage title={"Сферы деятельности"}>
				<div className={sass.head}>
					<Button disabled={disabled} onClick={() => setModal({ active: true, data: null })}>
						Добавить значение
					</Button>
				</div>
			</HeadPage>
			<table>
				<thead>
					<tr>
						<th>Порядковый номер</th>
						<th>Значение</th>
						<th />
					</tr>
				</thead>
			</table>
			<div className={sass.scroll}>
				<table data-comment-false={true}>
					<tbody>
						{filter
							? filter.map((_, index) => {
									const classRow = outlineStyle(_.id || 0);
									return (
										<tr
											className={classRow}
											onClick={() => (disabled ? needRule() : setModal({ active: true, data: _ }))}
											key={index}>
											<td>{index + 1}</td>
											<td>{_.name}</td>
											<td>
												<ActionMenuIcon data-id={_.id} onClick={toggleTarget} />
											</td>
										</tr>
									);
							  })
							: data.map((_, index) => {
									const classRow = outlineStyle(_.id || 0);
									return (
										<tr
											className={classRow}
											onClick={() => (disabled ? needRule() : setModal({ active: true, data: _ }))}
											key={index}>
											<td>{index + 1}</td>
											<td>{_.name}</td>
											<td>
												<ActionMenuIcon data-id={_.id} onClick={toggleTarget} />
											</td>
										</tr>
									);
							  })}
					</tbody>
				</table>
			</div>
		</div>
	);
};

export default AreasOfActivity;
